<template>
  <div class="attendee-card">
    <!-- Header with photo and basic info -->
    <div class="attendee-header">
      <div class="attendee-photo">
        <img 
          v-if="attendee.profile_image_url" 
          :src="attendee.profile_image_url" 
          :alt="`${attendee.name} profile photo`"
          @error="handleImageError"
        />
        <div v-else class="photo-placeholder">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </div>
      </div>
      
      <div class="attendee-info">
        <h4 class="attendee-name">{{ attendee.name }}</h4>
        <p v-if="attendee.title" class="attendee-title">{{ attendee.title }}</p>
        <p v-if="attendee.company" class="attendee-company">{{ attendee.company }}</p>
        
        <!-- Speaker badge -->
        <div v-if="attendee.is_speaker" class="speaker-badge">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
          </svg>
          Speaker
        </div>
      </div>
      
      <!-- Connection status indicator -->
      <div class="connection-status" :class="attendee.connection_status">
        <span class="status-dot"></span>
        <span class="status-text">{{ getConnectionStatusText(attendee.connection_status) }}</span>
      </div>
    </div>

    <!-- Speaker sessions (if applicable) -->
    <div v-if="attendee.is_speaker && getSpeakerSessions().length > 0" class="speaker-sessions">
      <h5 class="sessions-title">Speaking Sessions:</h5>
      <div class="sessions-list">
        <div 
          v-for="session in getSpeakerSessions()" 
          :key="session.id || session.session_id"
          class="session-item"
          @click="handleSessionClick(session)"
        >
          <div class="session-time">{{ formatSessionDateTime(session) }}</div>
          <div class="session-details">
            <div class="session-title">{{ session.name || session.title }}</div>
            <div v-if="getSessionLocation(session)" class="session-location">{{ getSessionLocation(session) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action buttons -->
    <div v-if="attendee.available_actions && attendee.available_actions.length > 0" class="attendee-actions">
      <button
        v-for="action in getDisplayedActions()"
        :key="action"
        @click="handleActionClick(action)"
        class="action-btn"
        :class="[`action-${action}`]"
      >
        <span class="action-icon">
          <svg v-if="action === 'view_profile'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
          <svg v-else-if="action === 'send_message'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="m3 3 3 9-3 9 19-9Z"></path>
            <path d="m6 12 13 0"></path>
          </svg>
          <svg v-else-if="action === 'add_connection'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <line x1="19" y1="8" x2="19" y2="14"></line>
            <line x1="22" y1="11" x2="16" y2="11"></line>
          </svg>
          <svg v-else-if="action === 'view_sessions'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <svg v-else-if="action === 'schedule_meeting'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
            <path d="M8 14h.01"></path>
            <path d="M12 14h.01"></path>
            <path d="M16 14h.01"></path>
            <path d="M8 18h.01"></path>
            <path d="M12 18h.01"></path>
            <path d="M16 18h.01"></path>
          </svg>
        </span>
        {{ getActionLabel(action) }}
      </button>
    </div>

    <!-- Match score indicator (if applicable) -->
    <div v-if="attendee.match_score !== undefined" class="match-score">
      Match: {{ Math.round(attendee.match_score * 100) }}%
    </div>
  </div>
</template>

<script>
import { formatTime, formatDate, formatFullDateTime } from '../../utils/datetime';

export default {
  name: 'AttendeeCard',
  props: {
    attendee: {
      type: Object,
      required: true
    },
    timezone: {
      type: String,
      default: 'UTC'
    },
    maxActions: {
      type: Number,
      default: 3
    }
  },
  emits: ['action-click', 'session-click'],
  methods: {
    handleImageError(event) {
      event.target.style.display = 'none';
    },
    
    getConnectionStatusText(status) {
      const statusMap = {
        'available': 'Available',
        'busy': 'Busy',
        'offline': 'Offline',
        'connected': 'Connected'
      };
      return statusMap[status] || 'Unknown';
    },
    
    getDisplayedActions() {
      if (!this.attendee.available_actions) return [];
      return this.attendee.available_actions.slice(0, this.maxActions);
    },
    
    getActionLabel(action) {
      const actionLabels = {
        'view_profile': 'View Profile',
        'send_message': 'Message',
        'add_connection': 'Connect',
        'view_sessions': 'Sessions',
        'schedule_meeting': 'Schedule'
      };
      return actionLabels[action] || action.replace('_', ' ');
    },
    
    formatSessionDateTime(session) {
      // Prefer using start_datetime with timezone for accurate display
      if (session.start_datetime) {
        const date = formatDate(session.start_datetime, this.timezone, 'ddd, MMM D');
        const time = formatTime(session.start_datetime, this.timezone);
        return `${date} ${time}`;
      }
      
      // Fallback to legacy format
      if (session.date && session.start_time) {
        const date = new Date(session.date);
        const dateStr = date.toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        });
        return `${dateStr} ${session.start_time}`;
      }
      
      // If we have datetime field (formatted string)
      if (session.datetime) {
        return session.datetime;
      }
      
      // Final fallback
      return session.start_time || 'TBD';
    },
    
    handleActionClick(action) {
      this.$emit('action-click', {
        action,
        attendee: this.attendee
      });
    },
    
    handleSessionClick(session) {
      this.$emit('session-click', {
        session,
        attendee: this.attendee
      });
    },
    
    getSpeakerSessions() {
      // Support both schedule_items (new format) and speaker_sessions (legacy)
      return this.attendee.schedule_items || this.attendee.speaker_sessions || [];
    },
    
    getSessionLocation(session) {
      // Handle both new format (locations array) and legacy format (location string)
      if (session.locations && session.locations.length > 0) {
        return session.locations[0];
      }
      return session.location || null;
    }
  }
};
</script>

<style scoped>
.attendee-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  background-color: #fff;
  transition: all 0.2s ease;
  margin-bottom: 12px;
}

.attendee-card:hover {
  border-color: #adb5bd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.attendee-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  position: relative;
}

.attendee-photo {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 12px;
  flex-shrink: 0;
  background-color: #f8f9fa;
  border: 2px solid #e9ecef;
}

.attendee-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
}

.attendee-info {
  flex-grow: 1;
  min-width: 0;
}

.attendee-name {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #212529;
  line-height: 1.3;
}

.attendee-title {
  font-size: 0.85rem;
  color: #6c757d;
  margin: 0 0 2px 0;
  font-weight: 500;
}

.attendee-company {
  font-size: 0.8rem;
  color: #8e9197;
  margin: 0 0 6px 0;
}

.speaker-badge {
  display: inline-flex;
  align-items: center;
  background-color: #fff3cd;
  color: #856404;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
  border: 1px solid #ffeaa7;
}

.speaker-badge svg {
  margin-right: 3px;
  fill: currentColor;
}

.connection-status {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  margin-left: 8px;
  flex-shrink: 0;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
}

.connection-status.available .status-dot {
  background-color: #198754;
}

.connection-status.busy .status-dot {
  background-color: #ffc107;
}

.connection-status.offline .status-dot {
  background-color: #6c757d;
}

.connection-status.connected .status-dot {
  background-color: #0d6efd;
}

.status-text {
  color: #6c757d;
  font-weight: 500;
}

.speaker-sessions {
  margin-bottom: 12px;
  padding: 8px 0;
  border-top: 1px solid #f1f3f4;
}

.sessions-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  margin: 0 0 6px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.session-item {
  display: flex;
  align-items: flex-start;
  padding: 6px 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.session-item:hover {
  background-color: #e9ecef;
}

.session-time {
  font-size: 0.75rem;
  font-weight: 600;
  color: #0d6efd;
  margin-right: 8px;
  flex-shrink: 0;
  min-width: 90px;
}

.session-details {
  flex-grow: 1;
  min-width: 0;
}

.session-title {
  font-size: 0.8rem;
  font-weight: 500;
  color: #212529;
  line-height: 1.3;
  margin-bottom: 2px;
}

.session-location {
  font-size: 0.7rem;
  color: #6c757d;
}

.attendee-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f3f4;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #fff;
  color: #495057;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.action-btn:hover {
  border-color: #adb5bd;
  background-color: #f8f9fa;
  transform: translateY(-1px);
}

.action-icon {
  margin-right: 4px;
  display: flex;
  align-items: center;
}

.action-view_profile {
  color: #0d6efd;
  border-color: #b6d4fe;
}

.action-view_profile:hover {
  background-color: #cfe2ff;
  border-color: #9ec5fe;
}

.action-send_message {
  color: #198754;
  border-color: #b8dfcb;
}

.action-send_message:hover {
  background-color: #d1e7dd;
  border-color: #a3cfbb;
}

.action-add_connection {
  color: #6f42c1;
  border-color: #d0bfff;
}

.action-add_connection:hover {
  background-color: #e7d6ff;
  border-color: #c7a8ff;
}

.match-score {
  font-size: 0.7rem;
  color: #6c757d;
  text-align: right;
  margin-top: 8px;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .attendee-card {
    padding: 12px;
  }
  
  .attendee-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .connection-status {
    margin-left: 0;
    margin-top: 4px;
  }
  
  .attendee-photo {
    width: 40px;
    height: 40px;
    margin-bottom: 8px;
  }
  
  .action-btn {
    font-size: 0.7rem;
    padding: 3px 6px;
  }
}
</style>